import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Chip, Skeleton, Stack, Tooltip, } from '@mui/material';
import { getVariantImageUrl } from './variantValidationUtils';
import { replaceStylesheetGlobals } from '../../portal/theme/portalTheme';
import useIsMobileView from '../hooks/useIsMobileView';
// Simple fallback SVG generator
const createFallbackSVG = (value) => `data:image/svg+xml;base64,${btoa(`
  <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
    <rect width="50" height="50" fill="#f5f5f5" stroke="#ddd" stroke-width="1"/>
    <text x="25" y="30" text-anchor="middle" font-family="Arial, sans-serif" font-size="8" fill="#666">${value}</text>
  </svg>
`)}`;
// Helper function to detect invalid selections (refined algorithm)
const isSelectionInvalid = (selectedOptions, variants, optionNames) => {
    // If selection is incomplete, it's not invalid (still in progress)
    const requiredGroups = optionNames.length;
    const selectedGroups = Object.keys(selectedOptions).length;
    if (selectedGroups < requiredGroups)
        return false;
    // Find if there's an available variant that matches the selection
    const matchingVariant = variants.find((variant) => optionNames.every((optionName, index) => {
        const selectedValue = selectedOptions[optionName];
        return selectedValue && variant.options?.[index] === selectedValue;
    }));
    // Selection is invalid if:
    // 1. Complete selection (all groups selected) AND
    // 2. No matching variant OR matching variant is not available
    return !matchingVariant || matchingVariant.available === false;
};
// MULTIPHOTO THUMBNAILS: Filter by stock (makes sense for images)
const computeInStock = (variants) => variants.filter((v) => v.available !== false);
const checkGlobalAvailability = (inStock, optionNames) => {
    const globalAvail = {};
    optionNames.forEach((groupName, groupIndex) => {
        globalAvail[groupName] = {};
        // Mark each value as available
        inStock.forEach((variant) => {
            const value = variant.options?.[groupIndex];
            if (value) {
                globalAvail[groupName][value] = true;
            }
        });
    });
    return globalAvail;
};
const isOptionCompatibleNow = (inStock, optionNames, currentSelection, groupName, value) => {
    const gi = optionNames.indexOf(groupName);
    if (gi === -1)
        return false;
    // Build required constraints = previously selected groups + the proposed (group,value)
    const required = { ...currentSelection, [groupName]: value };
    // A variant is compatible if it satisfies all constraints for selected groups
    return inStock.some((v) => optionNames.every((name, idx) => {
        const req = required[name];
        if (req == null || req === '')
            return true; // pristine group → unconstrained
        return v.options?.[idx] === req;
    }));
};
const computeOptionState = (params) => {
    const { isSelected, isGloballyAvailable, compatibleNow, isCurrentSelectionInvalid, } = params;
    if (isSelected && (isCurrentSelectionInvalid || !compatibleNow))
        return 'selected-invalid';
    if (isSelected)
        return 'selected';
    if (!isGloballyAvailable)
        return 'unavailable';
    if (!compatibleNow)
        return 'soft-incompatible';
    return 'available';
};
const resolveOnClick = (variants, optionNames, currentSelection, groupName, value) => {
    const groupIndex = optionNames.indexOf(groupName);
    if (groupIndex === -1) {
        return { nextSelection: currentSelection, variantId: null, autoAdjusted: {} };
    }
    computeInStock(variants);
    // RESPECT USER CHOICE: Allow selection of any option, even if unavailable
    // RESPECT USER CHOICE: Simply update the selected option without filtering
    const nextSelection = {
        ...currentSelection,
        [groupName]: value,
    };
    // Find matching variant based on user selection (no auto-adjustments)
    const matchingVariant = variants.find((v) => (optionNames.every((name, idx) => {
        const selectedValue = nextSelection[name];
        return !selectedValue || selectedValue === '' || v.options?.[idx] === selectedValue;
    })));
    return {
        nextSelection,
        variantId: matchingVariant?.id?.toString() || null,
        autoAdjusted: {}, // Never auto-adjust - respect user choice
    };
};
const OptionTitle = ({ groupName, selectedValue }) => (_jsxs(Typography, { variant: "body2", sx: {
        fontWeight: 600,
        mb: 0.5,
        color: 'inherit',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
        letterSpacing: '0.5px',
    }, children: [groupName, ': ', _jsx("b", { children: selectedValue })] }));
const TextChips = ({ group, variants, selectedValue, spacing, globalAvailability, inStockVariants, optionNames, selectedVariantOptions, handleVariantOptionChange, onOptionHover, onOptionLeave, disableTooltip = false, }) => {
    // Helper function for text chip styles
    const getTextChipStyles = (state, chipVariant, disabled) => {
        const baseStyles = {
            minWidth: '34px',
            height: '28px',
            maxWidth: '80%',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease-in-out',
            opacity: (() => {
                if (state === 'selected-invalid')
                    return 0.6;
                if (state === 'soft-incompatible' || state === 'unavailable')
                    return 0.75;
                return 1;
            })(),
            position: 'relative',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
        };
        const labelStyles = {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
        };
        const stateStyles = (() => {
            switch (state) {
                case 'selected-invalid':
                    return {
                        backgroundColor: 'color_primary_disabled !important',
                        '& .MuiChip-label': {
                            color: 'inherit !important',
                            ...labelStyles,
                        },
                    };
                case 'selected':
                    return {
                        '& .MuiChip-label': labelStyles,
                    };
                default:
                    return {
                        color: 'inherit !important',
                        '& .MuiChip-label': {
                            color: 'inherit !important',
                            ...labelStyles,
                        },
                    };
            }
        })();
        let result = {
            ...baseStyles,
            ...stateStyles,
        };
        if (chipVariant === 'outlined') {
            result = { ...result, backgroundColor: 'transparent !important' };
        }
        if (state === 'soft-incompatible') {
            result = {
                ...result,
                borderStyle: 'dashed !important',
            };
        }
        return result;
    };
    return (_jsxs(Box, { children: [_jsx(OptionTitle, { groupName: group.name, selectedValue: selectedValue }), _jsx(Box, { sx: {
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    maxWidth: '100%',
                    width: '100%',
                    overflow: 'hidden',
                }, children: group.options.map((option) => {
                    const isSelected = selectedValue === option.value;
                    const isGloballyAvailable = globalAvailability[group.name]?.[option.value] || false;
                    const compatibleNow = isOptionCompatibleNow(inStockVariants, optionNames, selectedVariantOptions, group.name, option.value);
                    const selectionInvalid = isSelectionInvalid(selectedVariantOptions, variants, optionNames);
                    const state = computeOptionState({
                        isSelected,
                        isGloballyAvailable,
                        compatibleNow,
                        isCurrentSelectionInvalid: selectionInvalid,
                    });
                    // Visuals per state
                    const chipVariant = (state === 'selected' || state === 'selected-invalid') ? 'filled' : 'outlined';
                    const chipClassName = 'site-system-chip-primary';
                    const disabled = state === 'unavailable';
                    return (_jsx(Tooltip, { title: option.value, arrow: true, disableHoverListener: disableTooltip, disableFocusListener: disableTooltip, disableTouchListener: disableTooltip, children: _jsx(Chip, { "data-variant-pill": "true", "data-state": state, className: chipClassName, variant: chipVariant, disabled: disabled, onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleVariantOptionChange(group.name, option.value);
                            }, onMouseEnter: () => {
                                if (typeof onOptionHover === 'function') {
                                    const preview = resolveOnClick(variants, optionNames, selectedVariantOptions, group.name, option.value);
                                    onOptionHover({
                                        groupName: group.name,
                                        value: option.value,
                                        previewSelection: preview.nextSelection,
                                        previewVariantId: preview.variantId,
                                    });
                                }
                            }, onMouseLeave: () => {
                                if (typeof onOptionLeave === 'function')
                                    onOptionLeave();
                            }, sx: getTextChipStyles(state, chipVariant, disabled), label: option.value }) }, option.id));
                }) })] }, group.name));
};
const VariantSelector = ({ variantGroups, variants, dataSource, aiSearchConfig, selectedVariantOptions: externalSelectedVariantOptions, onVariantSelectionChange, onAutoAdjust, onOptionHover: onOptionHoverProp, onOptionLeave: onOptionLeaveProp, disableTooltip: disableTooltipProp = false, spacing = 1, enableImageSwatching = false, imageSwatchesConfig, stylesheet, isLoading, }) => {
    const isMobileView = useIsMobileView();
    const disableTooltip = disableTooltipProp || isMobileView;
    const onOptionHover = isMobileView ? undefined : onOptionHoverProp;
    const onOptionLeave = isMobileView ? undefined : onOptionLeaveProp;
    // Internal state for uncontrolled mode
    const [internalSelectedVariantOptions, setInternalSelectedVariantOptions] = useState({});
    const [, setInternalSelectedVariantId] = useState(null);
    // Determine if we're in controlled or uncontrolled mode
    const isControlled = externalSelectedVariantOptions !== undefined;
    const selectedVariantOptions = isControlled
        ? externalSelectedVariantOptions
        : internalSelectedVariantOptions;
    // Get option names from dataSource
    const optionNames = useMemo(() => dataSource?.options?.map((option) => option.name) || [], [dataSource?.options]);
    // Smart image-variant detection algorithm
    const imageThumbsConfig = useMemo(() => {
        const result = {
            groupName: null,
            valueToThumbSrc: {},
        };
        const opts = dataSource?.options || [];
        if (!opts.length || !variants.length) {
            return result;
        }
        // Helper function to calculate image coverage for a group
        function calculateGroupCoverage(groupIndex) {
            const sourceVariants = variants.filter((v) => v.available !== false);
            const availableValues = new Set();
            const uniqueImageSrcs = new Set();
            const valueToImageMap = new Map();
            sourceVariants.forEach((v) => {
                const val = v.options?.[groupIndex];
                if (val) {
                    availableValues.add(val);
                    let src = null;
                    src = getVariantImageUrl(v, dataSource, undefined);
                    if (src && typeof src === 'string' && src.trim() !== '') {
                        uniqueImageSrcs.add(src);
                        // Map each value to its image (first image wins)
                        if (!valueToImageMap.has(val)) {
                            valueToImageMap.set(val, src);
                        }
                    }
                }
            });
            // Coverage = values with images / total values (max 100%)
            const valuesWithImages = Array.from(valueToImageMap.keys()).length;
            const coverage = availableValues.size > 0
                ? Math.min(valuesWithImages / availableValues.size, 1.0)
                : 0;
            // Usefulness = distinct images / total values (max 100%)
            const distinctImageValues = new Set(valueToImageMap.values()).size;
            const usefulness = availableValues.size > 0
                ? Math.min(distinctImageValues / availableValues.size, 1.0)
                : 0;
            return {
                coverage,
                usefulness,
                availableValues: availableValues.size,
                valuesWithImages,
                distinctImageValues,
                uniqueImageSrcs: uniqueImageSrcs.size,
            };
        }
        // 1. Calculate coverage for all groups
        const groupCoverages = opts.map((o, i) => {
            const coverage = calculateGroupCoverage(i);
            return {
                index: i,
                name: o.name,
                isColorish: /color|colour|colorway|colourway/i.test(o.name),
                ...coverage,
            };
        });
        // 2. Find best group: prioritize groups with multiple values and images
        // First try to find groups with good coverage
        let qualifyingGroups = groupCoverages.filter((g) => {
            const passes = g.coverage >= 0.6
                && g.usefulness >= 0.6
                && g.availableValues >= 1;
            return passes;
        });
        // If no groups with good coverage, fall back to any groups with images
        if (qualifyingGroups.length === 0) {
            qualifyingGroups = groupCoverages.filter((g) => {
                const passes = g.valuesWithImages > 0
                    && g.availableValues >= 1;
                return passes;
            });
        }
        if (qualifyingGroups.length === 0) {
            return result;
        }
        // Sort by usefulness (descending), then by coverage, then by colorish preference
        const bestGroup = qualifyingGroups.sort((a, b) => {
            if (a.usefulness !== b.usefulness) {
                return b.usefulness - a.usefulness; // Higher usefulness first
            }
            if (a.coverage !== b.coverage) {
                return b.coverage - a.coverage; // Higher coverage second
            }
            // Final tiebreaker: prefer colorish groups
            return Number(b.isColorish) - Number(a.isColorish);
        })[0];
        const idx = bestGroup.index;
        const group = opts[idx];
        // 3. Build value → thumbnail mapping with deduplication and fallbacks
        const map = {};
        const seenSrcs = new Set();
        const sourceVariants = variants.filter((v) => v.available !== false);
        const availableValues = new Set();
        // First pass: collect all available values
        sourceVariants.forEach((v) => {
            const val = v.options?.[idx];
            if (val)
                availableValues.add(val);
        });
        // Second pass: try to get images for each value
        sourceVariants.forEach((v) => {
            const val = v.options?.[idx];
            const media = v.featured_media;
            if (!val)
                return;
            const src = media?.preview_image?.src || media?.src || media?.url;
            if (src && src.trim() !== '' && !seenSrcs.has(src)) {
                map[val] = src;
                seenSrcs.add(src);
            }
        });
        // Third pass: add fallbacks for values without images
        availableValues.forEach((val) => {
            if (!map[val]) {
                map[val] = createFallbackSVG(val);
            }
        });
        const finalResult = {
            groupName: String(group.name),
            valueToThumbSrc: map,
        };
        return finalResult;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource?.options, variants]);
    // Compute in-stock variants
    const inStockVariants = useMemo(() => computeInStock(variants), [variants]);
    // Pre-calculate global availability
    const globalAvailability = useMemo(() => checkGlobalAvailability(inStockVariants, optionNames), [inStockVariants, optionNames]);
    // Auto-select groups that have only one available option
    useEffect(() => {
        // Skip if controlled mode - parent manages selections
        if (isControlled)
            return;
        // Skip if we already have some selections to avoid overriding user choices
        if (Object.keys(selectedVariantOptions).length > 0)
            return;
        const autoSelections = {};
        variantGroups.forEach((group) => {
            // Find available options for this group
            const availableOptions = group.options.filter((option) => {
                const availability = globalAvailability[group.name]?.[option.value];
                return availability === true;
            });
            // If exactly one option is available, auto-select it
            if (availableOptions.length === 1) {
                autoSelections[group.name] = availableOptions[0].value;
            }
        });
        // Apply auto-selections if any were found
        if (Object.keys(autoSelections).length > 0) {
            setInternalSelectedVariantOptions(autoSelections);
            // Find the matching variant ID for the auto-selections
            const matchingVariant = variants.find((variant) => optionNames.every((optionName) => {
                const selectedValue = autoSelections[optionName];
                if (!selectedValue)
                    return true; // No selection for this option
                const variantOption = variant.options.find((opt) => opt.name === optionName);
                return variantOption?.value === selectedValue;
            }));
            setInternalSelectedVariantId(matchingVariant?.id || null);
            // Notify parent of auto-selection
            onVariantSelectionChange?.(autoSelections, matchingVariant?.id || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variantGroups, globalAvailability, isControlled]);
    const handleVariantOptionChange = (groupName, optionValue) => {
        const result = resolveOnClick(variants, optionNames, selectedVariantOptions, groupName, optionValue);
        if (isControlled) {
            // Controlled mode: notify parent
            onVariantSelectionChange?.(result.nextSelection, result.variantId);
        }
        else {
            // Uncontrolled mode: update internal state
            setInternalSelectedVariantOptions(result.nextSelection);
            setInternalSelectedVariantId(result.variantId);
        }
        if (onAutoAdjust && result.autoAdjusted && Object.keys(result.autoAdjusted).length > 0) {
            onAutoAdjust(result.autoAdjusted);
        }
    };
    // Helper function for image swatch styles
    const getImageSwatchStyles = (state, disabled, isSelected, swatchConfig, searchConfig, stylesheetData) => {
        const chipStyles = stylesheetData?.site?.system?.chips?.primary;
        const normalColor = chipStyles?.border?.color || 'color_primary';
        const selectedColor = chipStyles?.selectedColor || 'color_primary_hover';
        const disabledColor = chipStyles?.border?.disabled?.color || 'color_primary_disabled';
        const hoverColor = chipStyles?.border?.hover?.color || 'color_primary_hover';
        const baseStyles = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: swatchConfig?.size || searchConfig?.variantPhotoSwatches?.size || 50,
            height: swatchConfig?.size || searchConfig?.variantPhotoSwatches?.size || 50,
            borderRadius: 1,
            overflow: 'hidden',
            opacity: (() => {
                if (state === 'selected-invalid')
                    return 0.6;
                if (state === 'soft-incompatible' || state === 'unavailable')
                    return 0.75;
                return 1;
            })(),
            cursor: disabled ? 'not-allowed' : 'pointer',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            backgroundColor: 'transparent',
            border: isSelected ? '3px solid' : '1px solid',
            borderColor: (() => {
                if (!stylesheetData || !chipStyles) {
                    if (disabled)
                        return '#091E424F';
                    if (isSelected)
                        return '#005c8c';
                    return '#007bad';
                }
                if (disabled) {
                    return replaceStylesheetGlobals(stylesheetData, disabledColor);
                }
                if (isSelected) {
                    return replaceStylesheetGlobals(stylesheetData, selectedColor);
                }
                return replaceStylesheetGlobals(stylesheetData, normalColor);
            })(),
        };
        let result = { ...baseStyles };
        if (!disabled && !isSelected && stylesheetData && chipStyles) {
            result = {
                ...result,
                '&:hover': {
                    borderColor: replaceStylesheetGlobals(stylesheetData, hoverColor),
                },
            };
        }
        if (state === 'soft-incompatible') {
            result = {
                ...result,
                borderStyle: 'dashed',
                borderColor: 'warning.main',
            };
        }
        if (state === 'selected-invalid') {
            result = {
                ...result,
                borderColor: stylesheetData && chipStyles ? replaceStylesheetGlobals(stylesheetData, disabledColor) : '#091E424F',
                backgroundColor: stylesheetData && chipStyles ? replaceStylesheetGlobals(stylesheetData, disabledColor) : '#091E424F',
            };
        }
        return result;
    };
    const renderThumbnailPills = (group, selectedValue) => (_jsxs(Box, { children: [_jsx(OptionTitle, { groupName: group.name, selectedValue: selectedValue }), _jsx(Box, { sx: { display: 'flex', gap: (imageSwatchesConfig?.gap || aiSearchConfig?.variantPhotoSwatches?.gap || 8) / 8, flexWrap: 'wrap' }, children: group.options
                    .filter((option) => imageThumbsConfig.valueToThumbSrc?.[option.value])
                    .map((option) => {
                    const isSelected = selectedValue === option.value;
                    const isGloballyAvailable = globalAvailability[group.name]?.[option.value] || false;
                    const compatibleNow = isOptionCompatibleNow(inStockVariants, optionNames, selectedVariantOptions, group.name, option.value);
                    const state = computeOptionState({
                        isSelected,
                        isGloballyAvailable,
                        compatibleNow,
                    });
                    const disabled = state === 'unavailable';
                    const thumb = imageThumbsConfig.valueToThumbSrc?.[option.value];
                    return (_jsx(Tooltip, { title: option.value, arrow: true, disableHoverListener: disableTooltip, disableFocusListener: disableTooltip, disableTouchListener: disableTooltip, children: _jsxs(Box, { role: disabled ? undefined : 'button', tabIndex: disabled ? -1 : 0, "aria-label": `Select ${option.value} ${group.name}`, onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleVariantOptionChange(group.name, option.value);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleVariantOptionChange(group.name, option.value);
                                }
                            }, onMouseEnter: () => {
                                if (typeof onOptionHover === 'function') {
                                    const preview = resolveOnClick(variants, optionNames, selectedVariantOptions, group.name, option.value);
                                    onOptionHover({
                                        groupName: group.name,
                                        value: option.value,
                                        previewSelection: preview.nextSelection,
                                        previewVariantId: preview.variantId,
                                    });
                                }
                            }, onMouseLeave: () => {
                                if (typeof onOptionLeave === 'function')
                                    onOptionLeave();
                            }, sx: getImageSwatchStyles(state, disabled, isSelected, imageSwatchesConfig, aiSearchConfig, stylesheet), children: [_jsx("img", { src: thumb, alt: option.value, style: { width: '100%', height: '100%', objectFit: 'cover' }, onError: (e) => {
                                        // If image fails, hide this option entirely (no fallback in thumbnail mode)
                                        console.warn('[VariantSelector] Image failed to load, hiding option:', thumb);
                                        e.currentTarget.closest('[role="button"]')?.remove();
                                    } }), isSelected && (_jsx(Box, { sx: {
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        color: (() => {
                                            const chipStyles = stylesheet?.site?.system?.chips?.primary;
                                            const selectedColor = chipStyles?.selectedColor || 'color_primary_hover';
                                            if (stylesheet && chipStyles) {
                                                return replaceStylesheetGlobals(stylesheet, selectedColor);
                                            }
                                            return '#005c8c'; // fallback
                                        })(),
                                        fontSize: '1.2rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 1,
                                        fontWeight: 700,
                                        textShadow: '0 0 3px rgba(255,255,255,0.8), 0 0 2px rgba(255,255,255,0.9)',
                                    }, children: "\u2713" }))] }) }, option.id));
                }) })] }, group.name));
    // Auto-select single available options
    // (moved from render to useEffect to avoid setState during render)
    useEffect(() => {
        if (isControlled) {
            // For controlled mode, check each group and auto-select if needed
            variantGroups.forEach((group) => {
                const selectedValue = selectedVariantOptions[group.name] || '';
                const availableOptions = group.options.filter((option) => {
                    const availability = globalAvailability[group.name]?.[option.value];
                    return availability === true;
                });
                if (availableOptions.length === 1 && !selectedValue) {
                    // Auto-select the only available option
                    onVariantSelectionChange?.({ ...selectedVariantOptions, [group.name]: availableOptions[0].value }, null);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variantGroups, globalAvailability, isControlled, selectedVariantOptions]);
    const renderVariantPills = (group) => {
        const selectedValue = selectedVariantOptions[group.name] || '';
        // Check if this group should render as thumbnails
        // Only render as thumbnails if:
        // 1. Image swatching is enabled globally
        // 2. This group matches the detected image group
        // 3. The group name includes "color" or "colour" (e.g. "Apparel Color")
        // 4. There are actual swatch images defined
        const isColorGroup = /color|colour/i.test(group.name);
        const hasSwatchesDefined = imageThumbsConfig.groupName
            && group.name === imageThumbsConfig.groupName
            && Object.keys(imageThumbsConfig.valueToThumbSrc || {}).length > 0;
        const renderAsThumbnails = enableImageSwatching
            && imageThumbsConfig.groupName
            && group.name === imageThumbsConfig.groupName
            && isColorGroup
            && hasSwatchesDefined;
        if (renderAsThumbnails) {
            return renderThumbnailPills(group, selectedValue);
        }
        // Default: render as text chips (existing logic)
        return (_jsx(TextChips, { group: group, variants: variants, selectedValue: selectedValue, spacing: spacing, globalAvailability: globalAvailability, inStockVariants: inStockVariants, optionNames: optionNames, selectedVariantOptions: selectedVariantOptions, handleVariantOptionChange: handleVariantOptionChange, onOptionHover: onOptionHover || (() => { }), onOptionLeave: onOptionLeave || (() => { }), disableTooltip: disableTooltip }));
    };
    if (isLoading) {
        return _jsx(Skeleton, { variant: "text", width: "100%" });
    }
    if (variantGroups.length === 0) {
        return null;
    }
    return (_jsx(Stack, { direction: "column", spacing: spacing, children: variantGroups.map((group) => (_jsx(React.Fragment, { children: renderVariantPills(group) }, group.name || group.id || `group-${variantGroups.indexOf(group)}`))) }));
};
VariantSelector.defaultProps = {
    aiSearchConfig: undefined,
    selectedVariantOptions: undefined,
    onVariantSelectionChange: undefined,
    onAutoAdjust: undefined,
    onOptionHover: undefined,
    onOptionLeave: undefined,
    disableTooltip: false,
    spacing: 1,
    enableImageSwatching: false,
    imageSwatchesConfig: undefined,
    stylesheet: undefined,
};
export default VariantSelector;
//# sourceMappingURL=VariantSelector.js.map